import { keyframes } from '@mui/material/styles'

/**
 * Mobile-optimized animation utilities
 * Provides smooth, performant animations for mobile devices
 */

// Slide animations
export const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`

export const slideInFromLeft = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`

export const slideInFromBottom = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

export const slideInFromTop = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

// Fade animations
export const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

export const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

// Scale animations
export const scaleIn = keyframes`
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`

export const scaleOut = keyframes`
  from {
    transform: scale(1);
    opacity: 1;
  }
  to {
    transform: scale(0.9);
    opacity: 0;
  }
`

// Bounce animation
export const bounceIn = keyframes`
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`

// Shake animation
export const shake = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-10px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(10px);
  }
`

// Pulse animation
export const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`

// Ripple animation
export const ripple = keyframes`
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(4);
    opacity: 0;
  }
`

// Shimmer animation (for loading states)
export const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`

// Rotate animation
export const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

/**
 * Animation timing functions
 */
export const easings = {
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
}

/**
 * Animation durations (in ms)
 */
export const durations = {
  shortest: 150,
  shorter: 200,
  short: 250,
  standard: 300,
  complex: 375,
  enteringScreen: 225,
  leavingScreen: 195,
}

/**
 * Helper function to create animation styles
 */
export const createAnimation = (
  animationName: ReturnType<typeof keyframes>,
  duration: number = durations.standard,
  easing: string = easings.easeInOut,
  delay: number = 0
) => ({
  animation: `${animationName} ${duration}ms ${easing} ${delay}ms both`,
})

/**
 * Stagger animation helper
 * Creates staggered animations for list items
 */
export const createStaggerAnimation = (
  index: number,
  baseDelay: number = 50,
  animationName: ReturnType<typeof keyframes> = fadeIn
) => ({
  animation: `${animationName} ${durations.standard}ms ${easings.easeOut} ${index * baseDelay}ms both`,
})

/**
 * Page transition animations
 */
export const pageTransitions = {
  slideRight: {
    enter: createAnimation(slideInFromRight, durations.enteringScreen),
    exit: createAnimation(slideInFromLeft, durations.leavingScreen),
  },
  slideLeft: {
    enter: createAnimation(slideInFromLeft, durations.enteringScreen),
    exit: createAnimation(slideInFromRight, durations.leavingScreen),
  },
  slideUp: {
    enter: createAnimation(slideInFromBottom, durations.enteringScreen),
    exit: createAnimation(slideInFromTop, durations.leavingScreen),
  },
  fade: {
    enter: createAnimation(fadeIn, durations.enteringScreen),
    exit: createAnimation(fadeOut, durations.leavingScreen),
  },
  scale: {
    enter: createAnimation(scaleIn, durations.enteringScreen),
    exit: createAnimation(scaleOut, durations.leavingScreen),
  },
}

/**
 * Card entrance animations
 */
export const cardAnimations = {
  fadeIn: createAnimation(fadeIn, durations.standard),
  scaleIn: createAnimation(scaleIn, durations.standard),
  bounceIn: createAnimation(bounceIn, durations.complex),
  slideUp: createAnimation(slideInFromBottom, durations.standard),
}

/**
 * Loading animations
 */
export const loadingAnimations = {
  shimmer: {
    background: 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)',
    backgroundSize: '1000px 100%',
    animation: `${shimmer} 2s infinite linear`,
  },
  pulse: createAnimation(pulse, 1500, easings.easeInOut),
  rotate: createAnimation(rotate, 1000, 'linear'),
}

/**
 * Micro-interaction animations
 */
export const microAnimations = {
  tap: {
    transform: 'scale(0.98)',
    transition: `transform ${durations.shortest}ms ${easings.sharp}`,
  },
  hover: {
    transform: 'scale(1.02)',
    transition: `transform ${durations.short}ms ${easings.easeOut}`,
  },
  shake: createAnimation(shake, durations.complex),
  pulse: createAnimation(pulse, durations.standard),
}
